<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, onMounted, onUnmounted, watch, nextTick, provide } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'
import MediaflowService from '@/services/MediaflowService'
import { createFlowWithMedia } from '@/components/Mediaflow/createFlowWithMedia'
import StreamStatsService from '@/services/StreamStatsService'
import { useMatchMedia } from '@/utils'
import PageHeader from '@/components/ui/PageHeader.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import BackButton from '@/components/Atoms/BackButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Title from '@/components/ui/Title.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconElipsisVertical from '@/components/icon/IconElipsisVertical.vue'
import IconLock from '@/components/icon/IconLock.vue'
import IconStackedLayers from '@/components/icon/IconStackedLayers.vue'
import IconOpenExternal from '@/components/icon/IconOpenExternal.vue'
import IconArrowRight from '@/components/icon/IconArrowRight.vue'
import IconCog from '@/components/icon/IconCog.vue'
import IconEdit from '@/components/icon/IconEdit.vue'
import IconCalendarOutline from '@/components/icon/IconCalendarOutline.vue'
import IconMediaflowLockedPlaceholderImage from '@/components/icon/IconMediaflowLockedPlaceholderImage.vue'
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import AddFlowModal from '@/components/modals/AddFlowModal.vue'
import EditFlowModal from '@/components/modals/EditFlowModal.vue'
import AddFlowMediaModal from '@/components/modals/AddFlowMediaModal.vue'
import EditMediaflowModal from '@/components/modals/EditMediaflowModal.vue'
import MediaflowMonitor from '@/components/Mediaflow/MediaflowMonitor.vue'
import MediaflowEditorPanel from '@/components/Mediaflow/MediaflowEditorPanel.vue'
import MediaflowTransitionRail from '@/components/Mediaflow/MediaflowTransitionRail.vue'
import MediaflowScenesDock from '@/components/Mediaflow/MediaflowScenesDock.vue'
import MediaflowSourcesDock from '@/components/Mediaflow/MediaflowSourcesDock.vue'
import { useMediaflowSourceActions } from '@/components/Mediaflow/useMediaflowSourceActions'
import MediaflowGraphicsDock from '@/components/Mediaflow/MediaflowGraphicsDock.vue'
import MediaflowBreaksDock from '@/components/Mediaflow/MediaflowBreaksDock.vue'
import MediaflowSidePanel from '@/components/Mediaflow/MediaflowSidePanel.vue'
import MediaflowSessionChip from '@/components/Mediaflow/MediaflowSessionChip.vue'
import MediaflowShortcuts from '@/components/Mediaflow/MediaflowShortcuts.vue'
import MediaflowMediaPreviewModal from '@/components/Mediaflow/MediaflowMediaPreviewModal.vue'
import MediaflowGraphicSettingsModal from '@/components/Mediaflow/MediaflowGraphicSettingsModal.vue'
import MediaflowBreakSettingsModal from '@/components/Mediaflow/MediaflowBreakSettingsModal.vue'
import { useMediaflowDesk } from '@/components/Mediaflow/useMediaflowDesk'
import {
	parseScreenResolution, sessionWindow, outputHealth, overlayRows, productionInputs, cloneMediaPayload,
	lBandProgramTransform, productionMediaNames, uniqueMediaName, cueRemainingSec, upcomingScheduledEvents, formatDurationShort,
	mediaSubtitle, draftApplyLabel, scheduleLimitReached, scheduleLimitReason, flowErrorMessage,
} from '@/components/Mediaflow/mediaflowMeta'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { notify } = useNotification()
// Below this the canvas is view-only (touch + tiny handles don't mix) and the
// page becomes the simplified phone control room: Program, scenes, graphics, breaks.
const canEditCanvas = useMatchMedia('(min-width: 768px)')
const isMobile = computed(() => !canEditCanvas.value)

// ---------------------------------------------------------------- store state
const mediaflow = computed(() => store.getters['Mediaflows/mediaflow'])
const mediaflowReady = computed(
	() => String(mediaflow.value?._id || '') === String(route.params.id || '')
)
const detailResolving = ref(true)
const detailSyncing = ref(false)
const detailRequestToken = ref(0)
const maxMediaflows = computed(() => store.getters['User/maxMediaflows'])
// Plan gates (not available / restricted) wait for the user's plan: on a
// reload the production can arrive before the subscription does.
const planSettled = ref(false)
const planResolved = computed(() => !!store.getters['User/userLoaded'] || planSettled.value)
const ready = computed(() => !detailResolving.value && mediaflowReady.value && planResolved.value)
// Name for the loading skeleton, from the list cache when coming from the dashboard.
const skeletonName = computed(() => {
	if (mediaflowReady.value) return mediaflow.value?.name || ''
	const list = store.getters['Mediaflows/mediaflows'] || []
	return list.find((m) => String(m._id) === String(route.params.id))?.name || ''
})
// "No destination" is only true once the streams are known.
const streamsReady = ref(false)
const mediaflowResolution = computed(() => store.getters['User/mediaflowResolution'])
const maxMediaflowFlows = computed(() => store.getters['User/maxMediaflowFlows'])
const maxMediaflowMedia = computed(() => store.getters['User/maxMediaflowMedia'])
const isLiveSubscriptionExpired = computed(() => store.getters['User/isLiveSubscriptionExpired'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const mediaflowTrial = computed(() => store.getters['User/mediaflowTrial'])
const mediaflowAllowedFpsList = computed(() => store.getters['User/mediaflowAllowedFpsList'])
const maxMediaflowAdSchedule = computed(() => store.getters['User/maxMediaflowAdSchedule'])

const fpsOptions = computed(() => {
	const fpsList = mediaflowAllowedFpsList.value
	const fpsString = fpsList && fpsList.trim() ? fpsList : '25,30'
	return fpsString.split(',').map((fps) => fps.trim()).filter(Boolean)
})

// ---------------------------------------------------------------- ui state
const modalDEMOVideo = ref(null)
const actionsMenu = ref(null)
const runMenuAction = (fn) => {
	actionsMenu.value?.hide?.()
	fn()
}

const destination = ref(null)
const startStreamProcessing = ref(false)

const switchingFlowId = ref(null)
const slectedFlowMediaSource = ref(null)
const addFlowModalRef = ref(null)
const editFlowModalRef = ref(null)
const addFlowMediaModalRef = ref(null)
const addGraphicModalRef = ref(null)
const editMediaflowModalRef = ref(null)
const sidePanelRef = ref(null)
const flowToDelete = ref(null)
const flowToEdit = ref(null)
const mediaToDelete = ref({})

const adSchedules = ref([])
const adSchedulesLoading = ref(false)
const scheduleToDelete = ref(null)
const togglingScheduleId = ref(null)


const showMediaPlayerModal = ref(false)
const mediaPlayerUrl = ref('')
const openMediaPlayerSelected = ref({ name: '' })
const mediaPlayerStream = ref(null)
const mediaPlayerMediaPulse = ref({})
const mediaPlayerLoading = ref(false)

const mediaPulse = ref(null)
const scopeAlive = ref(true)
const mediaPulseTimeoutCtrl = ref(null)
const streamAlive = ref(false)
const lastProgramAliveAt = ref(null)

const mediaPulsePreview = ref(null)
const mediaPulsePreviewTimeoutCtrl = ref(null)
const streamAlivePreview = ref(false)
const lastPreviewAliveAt = ref(null)

// One shared wall clock for countdowns and "in 12m" labels.
const clockNow = ref(Date.now())
let clockTimer = null

const sesessionStateCleared = ref(false)

// Right side: Automation drawer (upcoming + full editor) and Settings (output encoder).
const sidePanelOpen = ref(false)
const sidePanelTab = ref('automation')
const openSidePanel = (tab) => { sidePanelTab.value = tab; sidePanelOpen.value = true }
const transitionLabel = computed(() => (desk.transitionType.value === 'Cut' ? 'Cut' : `Fade · ${parseInt(desk.transitionDuration.value, 10) || 300} ms`))

// ---------------------------------------------------------------- derived
// In saved order (see the desk); read lazily since the desk is created below.
const flows = computed(() => desk.flows.value)
const sessionActive = computed(() => !!mediaflow.value?.obsInstance)

// Editing / draft / Send-to-live / graphics / break engine shared with the pop-out window.
// Escape closes this page's modals even from a focused field (see ModalBox).
provide('modalEscapeFromFields', true)
provide('modalPlainBackdrop', true)

const desk = useMediaflowDesk({
	mediaflow,
	store,
	notify,
	sessionActive,
	channelName: `castr-mediaflow-${route.params.id}`,
	detailPending: detailSyncing,
})
const selectedFlowId = desk.editingFlowId
const selectedFlow = desk.editingFlow
const layouts = desk.layouts
const overlays = desk.overlays
const capabilities = desk.capabilities
const cue = desk.cue
const enabled = computed(() => !!mediaflow.value?.enabled)

const portraitResolutions = ['720 x 1280', '1080 x 1920']
const isPortraitResolution = computed(() => portraitResolutions.includes(mediaflow.value?.screenResolution))
const canvas = computed(() => parseScreenResolution(mediaflow.value?.screenResolution, mediaflow.value?.resolutionPreset))

const destinationStream = computed(() => {
	if (!destination.value) return null
	return allInOneStreams.value.find((stream) => stream._id === destination.value._id) || null
})
const destinationPending = computed(() => !!destination.value && !destinationStream.value && !streamsReady.value)
const destinationId = computed(() => {
	const d = mediaflow.value?.destination
	return String((d && typeof d === 'object' ? d._id : d) || '')
})

const disabledStreamIds = computed(() => {
	const ids = new Set()
	if (destinationStream.value?._id) ids.add(destinationStream.value._id)
	flows.value.forEach((flow) => {
		flow?.media?.forEach((mediaItem) => {
			if (mediaItem?.type === 'live') {
				const streamValue = mediaItem?.stream
				const streamId = typeof streamValue === 'object' && streamValue !== null
					? (streamValue._id || streamValue.id)
					: streamValue
				if (streamId) ids.add(streamId)
			}
		})
	})
	return Array.from(ids)
})

const anyAlive = computed(() => streamAlive.value || streamAlivePreview.value)
const outputLocked = computed(() => streamAlive.value || streamAlivePreview.value || sessionActive.value)

/** Reserved window of the running session (start, auto-stop, time left). */
const session = computed(() => sessionWindow(mediaflow.value, clockNow.value))

const programHealth = computed(() => outputHealth(mediaPulse.value, {
	sessionActive: sessionActive.value,
	enabled: !!mediaflow.value?.enabled,
	lastAliveAt: lastProgramAliveAt.value,
	now: clockNow.value,
}))


/** offline · starting · live · disabled — one status system (house StatusPill). */
const productionStatus = computed(() => {
	if (!mediaflow.value?.enabled) return 'disabled'
	if (sessionActive.value && anyAlive.value) return 'live'
	if (sessionActive.value) return 'starting'
	return 'offline'
})

const statusPill = computed(() => ({
	disabled: { label: 'Disabled', variant: 'muted', pulse: false },
	offline: { label: 'Offline', variant: 'muted', pulse: false },
	starting: { label: 'Starting', variant: 'warning', pulse: true },
	live: { label: 'Live', variant: 'success', pulse: true },
}[productionStatus.value]))

/** Offline, enabled and nothing blocking: Go live gets the attention ring. */
const goLiveAttention = computed(() => !sessionActive.value && enabled.value && !startBlockedReason.value && !startStreamProcessing.value)

const startBlockedReason = computed(() => {
	if (!mediaflow.value?.enabled) return 'Enable the production first.'
	if (!layouts.value.length) return 'Add a scene first.'
	if (!destination.value) return 'Choose a destination first.'
	return ''
})

const sourceSubtitle = (media) => mediaSubtitle(media, allInOneStreams.value)

// ---------------------------------------------------------------- pulse polling
// Tokens stop stale loops from rescheduling after an unsubscribe/resubscribe
// while their request was still in flight
let pulseLoopToken = 0
let pulsePreviewLoopToken = 0

const subscribeMediaPulse = () => {
	const streamKey = mediaflow.value?.key
	const streamEnabled = mediaflow.value?.enabled
	clearTimeout(mediaPulseTimeoutCtrl.value)
	const token = ++pulseLoopToken;
	(async function loop() {
		if (!scopeAlive.value || token !== pulseLoopToken) return
		let timeoutInterval = 5000
		const shouldUpdatePulse = streamEnabled
		if (document.hidden) timeoutInterval = 6000
		if (shouldUpdatePulse && !document.hasFocus()) timeoutInterval = 14000
		if (shouldUpdatePulse) {
			try {
				const pulseObject = await StreamStatsService.getStreamMediaPulse(streamKey)
				onMediaPulse(pulseObject)
			} catch (e) { /* keep polling */ }
		} else mediaPulse.value = null
		if (!scopeAlive.value || token !== pulseLoopToken) return
		mediaPulseTimeoutCtrl.value = setTimeout(loop, timeoutInterval)
	})()
}

const onMediaPulse = (pulseObject = {}) => {
	if (mediaPulse.value?.withoutPushStats) {
		pulseObject.push_stats = mediaPulse.value.push_stats
	}
	mediaPulse.value = pulseObject
	streamAlive.value = mediaflow.value.enabled && mediaPulse.value?.alive
	if (streamAlive.value) lastProgramAliveAt.value = Date.now()
}

const unsubscribeMediaPulse = () => {
	pulseLoopToken++
	clearTimeout(mediaPulseTimeoutCtrl.value)
}

const subscribeMediaPulsePreview = () => {
	const streamKey = mediaflow.value?.key + '_preview'
	const streamEnabled = mediaflow.value?.enabled
	clearTimeout(mediaPulsePreviewTimeoutCtrl.value)
	const token = ++pulsePreviewLoopToken;
	(async function loop() {
		if (!scopeAlive.value || token !== pulsePreviewLoopToken) return
		let timeoutInterval = 5000
		const shouldUpdatePulse = streamEnabled
		if (document.hidden) timeoutInterval = 6000
		if (shouldUpdatePulse && !document.hasFocus()) timeoutInterval = 14000
		if (shouldUpdatePulse) {
			try {
				const pulseObject = await StreamStatsService.getStreamMediaPulse(streamKey)
				onMediaPulsePreview(pulseObject)
			} catch (e) { /* keep polling */ }
		} else mediaPulsePreview.value = null
		if (!scopeAlive.value || token !== pulsePreviewLoopToken) return
		mediaPulsePreviewTimeoutCtrl.value = setTimeout(loop, timeoutInterval)
	})()
}

const onMediaPulsePreview = (pulseObject = {}) => {
	if (mediaPulsePreview.value?.withoutPushStats) {
		pulseObject.push_stats = mediaPulsePreview.value.push_stats
	}
	mediaPulsePreview.value = pulseObject
	streamAlivePreview.value = mediaflow.value.enabled && mediaPulsePreview.value?.alive
	if (streamAlivePreview.value) lastPreviewAliveAt.value = Date.now()
}

const unsubscribeMediaPulsePreview = () => {
	pulsePreviewLoopToken++
	clearTimeout(mediaPulsePreviewTimeoutCtrl.value)
}

/** Fresh set per call: `uniqueMediaName` adds to it. */
// Draft sources count too: a copy must not take a name the draft needs on Apply.
const takenMediaNames = () => productionMediaNames([...flows.value, ...desk.draftedFlows.value], overlays.value)
// Live cameras used by a scene, drafts included: the L-band break template squeezes one.
const liveInputs = computed(() => productionInputs(desk.draftedFlows.value).filter((i) => i.media.type === 'live'))
const sceneInputs = computed(() => productionInputs(layouts.value))

// ---------------------------------------------------------------- production actions
const toggleMediaflow = async () => {
	try {
		await MediaflowService.toggleMediaflow(mediaflow.value._id)
		store.dispatch('Mediaflows/toggleMediaflow', { id: mediaflow.value._id })
		notify({ group: 'success', text: `Cloud Production ${mediaflow.value.enabled ? 'enabled' : 'disabled'}` })
	} catch (err) {
		console.error('Failed to toggle mediaflow', err)
		notify({ group: 'error', text: err.message || 'Cloud Production source update failed' })
	}
}

const onMediaflowDelete = async () => {
	if (!mediaflow.value._id) return
	try {
		await MediaflowService.deleteMediaflow(mediaflow.value._id)
		store.dispatch('Mediaflows/removeMediaflow', { id: mediaflow.value._id })
		notify({ group: 'success', text: 'Cloud Production deleted' })
		router.push({ name: 'MediaflowDashboard' })
	} catch (err) {
		console.error('Failed to delete mediaflow', err)
		notify({ group: 'error', text: err.message || 'Cloud Production delete failed' })
	}
}

const onDeleteMediaflowRequest = () => showModal('delete-mediaflow-content')

const onNameChange = async (value) => {
	const newName = (value || '').trim()
	if (!newName || newName.length <= 1 || newName === mediaflow.value?.name) return
	try {
		await MediaflowService.updateMediaflows(mediaflow.value._id, { name: newName })
		store.dispatch('Mediaflows/setName', { id: mediaflow.value._id, name: newName })
		notify({ group: 'success', text: 'Production renamed' })
	} catch (err) {
		console.error('Failed to update mediaflow name', err)
		notify({ group: 'error', text: err.message || 'Unable to update the name.' })
	}
}

const confirmGoLive = () => showModal('confirm-go-live')

const onStreamStart = async () => {
	startStreamProcessing.value = true
	try {
		const res = await MediaflowService.startStream(mediaflow.value._id)
		notify({ group: 'success', text: "You're live. It keeps running if you close this tab." })
		if (res && res.data) {
			store.dispatch('Mediaflows/setObsInstance', {
				mediaflowId: mediaflow.value._id,
				obsInstance: res.data.id,
				flowInstanceReservedAt: res.data.flowInstanceReservedAt,
				flowInstanceReserveTimeSec: res.data.flowInstanceReserveTimeSec,
			})
			sesessionStateCleared.value = false
		}
	} catch (err) {
		console.error(err)
		notify({ group: 'error', text: err || 'Unable to start the stream.' })
	}
	startStreamProcessing.value = false
}

const clearLocalSession = (mediaflowId) => {
	store.dispatch('Mediaflows/clearObsInstance', mediaflowId)
	store.dispatch('Mediaflows/setCue', { mediaflowId, cue: null })
	if (obsCountdownInterval) clearInterval(obsCountdownInterval)
	if (obsClearTimeout) {
		clearTimeout(obsClearTimeout)
		obsClearTimeout = null
	}
	obsCountdown.value = 0
}

const onStreamStop = async () => {
	const mediaflowId = mediaflow.value._id
	startStreamProcessing.value = true
	try {
		await MediaflowService.stopStream(mediaflowId)
		notify({ group: 'success', text: 'Production stopped' })
		clearLocalSession(mediaflowId)
	} catch (err) {
		console.error(err)
		notify({ group: 'error', text: `Couldn't stop the production: ${err?.message || 'please try again.'}` })
		// Show what the server has now, so the status never claims a session that already ended.
		store.dispatch('Mediaflows/getMediaflow', mediaflowId).catch(() => {})
	}
	startStreamProcessing.value = false
}

const showStopStreamModal = () => showModal('confirm-stop-stream')
const onConfirmStopStream = () => onStreamStop()

// Send to live: a flow goes on air with the current transition (`/transition`
// records type/duration, `switchScene` switches). Before a session exists the
// same call only marks the flow active, so Go live opens with it.
const pendingSendFlowId = ref(null)
const doSendToLive = async (flowId = pendingSendFlowId.value || desk.editingFlowId.value) => {
	pendingSendFlowId.value = null
	if (!flowId) return
	switchingFlowId.value = flowId
	try {
		await desk.sendToLive(flowId)
	} finally {
		switchingFlowId.value = null
	}
}
const onSendToLive = (flow = desk.editingFlow.value) => {
	if (!enabled.value || !flow) return
	if (flow.active || flow.kind === 'break') return
	pendingSendFlowId.value = flow._id
	// An unapplied draft on the on-air flow: keep it or throw it away first.
	if (desk.hasOnAirDraft.value) {
		showModal('confirm-send-with-draft')
		return
	}
	void doSendToLive(flow._id)
}
const sendDiscardingDraft = () => {
	// Drops the edited flow's draft and the graphics draft…
	desk.discardDraft()
	// …and the on-air flow's, which may not be the one in Preview.
	const onAirId = desk.onAirFlow.value?._id
	if (onAirId) desk.clearDraft(onAirId)
	void doSendToLive()
}
const sendKeepingDraft = () => { void doSendToLive() }
const sendDisabledReason = computed(() => {
	if (!enabled.value) return 'Enable the production first.'
	if (!desk.editingFlow.value) return 'Pick a scene first.'
	if (desk.editingFlow.value.kind === 'break') return 'Cue breaks from the Breaks dock.'
	if (desk.editingIsOnAir.value) return 'Pick another scene to send live'
	return ''
})
// Phones have no Preview: a tap on a scene asks before it goes on Program.
const mobileSendTarget = ref(null)
const onMobileSceneTap = (flow) => {
	if (!enabled.value || !flow || flow.active) return
	mobileSendTarget.value = flow
	showModal('confirm-mobile-send')
}
const confirmMobileSend = () => {
	const flow = mobileSendTarget.value
	mobileSendTarget.value = null
	if (flow) onSendToLive(flow)
}
const onSceneSend = (flow) => (isMobile.value ? onMobileSceneTap(flow) : onSendToLive(flow))

// Pop-out control window (same auth, minimal chrome). Closing it never
// touches the production — it is just another remote.
const openPopout = () => {
	const href = router.resolve({ name: 'MediaflowPreviewPopout', params: { id: mediaflow.value._id } }).href
	const win = window.open(href, `castr-mf-preview-${mediaflow.value._id}`, 'popup=yes,width=1280,height=860')
	if (!win) notify({ group: 'error', text: 'Pop-up blocked. Allow pop-ups for this site.' })
}

// Keyboard (OBS-like): 1–9 = scene N → Preview, Space/Enter = Send to live,
// Shift+1–9 = toggle graphic N (Graphics dock order), B = end break,
// ⌘/Ctrl+Z = undo/redo, ⌘/Ctrl+Enter = apply the layout draft.
// The canvas (role="application") keeps plain keys for nudging, but ⌘ combos
// still reach us from it: focus sits there right after a drag.
const isTypingTarget = (el, fromCanvasOk = false) => {
	if (!el?.closest) return false
	if (el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="dialog"], .sidebar-container, .dropdown-wrapper')) return true
	return !fromCanvasOk && !!el.closest('[role="application"]')
}
const onGlobalKeydown = (event) => {
	const meta = event.metaKey || event.ctrlKey
	if (event.defaultPrevented || !mediaflowReady.value || isTypingTarget(event.target, meta)) return
	if (document.querySelector('[role="dialog"][aria-modal="true"], .modal-box-open')) return
	if (meta && (event.key === 'z' || event.key === 'Z')) {
		if (!desk.draftMode.value && !desk.pendingCount.value) return
		event.preventDefault()
		if (event.shiftKey) desk.redo()
		else desk.undo()
		return
	}
	if (meta && event.key === 'Enter') {
		if (!desk.pendingCount.value || desk.applying.value) return
		event.preventDefault()
		void desk.applyDraft()
		return
	}
	if (meta || event.altKey) return
	if (event.key === ' ' || event.key === 'Enter') {
		// Leave focused buttons/links alone so keyboard users can still activate them.
		if (event.target?.closest?.('button, a, [tabindex]')) return
		if (sendDisabledReason.value) return
		event.preventDefault()
		onSendToLive()
		return
	}
	if (event.key === 'b' || event.key === 'B') {
		if (cue.value) { event.preventDefault(); void desk.endBreak() }
		return
	}
	// Shift+digit arrives as the shifted symbol on most layouts; `code` is stable.
	const digit = /^Digit([1-9])$/.exec(event.code)?.[1] || (/^[1-9]$/.test(event.key) ? event.key : null)
	if (!digit) return
	if (event.shiftKey) {
		const overlay = overlayRows(overlays.value).flatMap((r) => r.items)[Number(digit) - 1]
		if (overlay && capabilities.value.overlays && enabled.value) { event.preventDefault(); void desk.takeOverlay(overlay) }
		return
	}
	const flow = layouts.value[Number(digit) - 1]
	if (flow && !isMobile.value) desk.selectFlow(flow._id)
}

const setDestination = async (stream) => {
	try {
		const destinationId = stream ? stream._id : null
		await MediaflowService.updateMediaflows(mediaflow.value._id, { destination: destinationId })
		destination.value = stream
		await store.dispatch('Mediaflows/setDestination', { id: mediaflow.value._id, destination: destinationId })
		await store.dispatch('Mediaflows/getMediaflow', mediaflow.value._id)
		notify({ group: 'success', text: destinationId ? 'Destination updated' : 'Destination removed' })
	} catch (err) {
		console.error('Failed to update destination', err)
		notify({ group: 'error', text: err.message || 'Destination update failed' })
	}
}

const openHelp = () => window.open('https://docs.castr.com/en/articles/12310739-cloud-production-live-switching-made-simple', '_blank', 'noopener')

const streamPageUrl = computed(() => (destinationStream.value ? `/app/livestreams/${destinationStream.value._id}` : ''))
const onStreamPage = () => {
	if (streamPageUrl.value) window.open(streamPageUrl.value, '_blank')
}

// ---------------------------------------------------------------- session countdown
const obsCountdown = ref(0)
let obsCountdownInterval = null
let obsClearTimeout = null
let countdownSeenRunning = false

const updateObsCountdown = () => {
	if (!mediaflow.value?.flowInstanceReservedAt || !mediaflow.value?.flowInstanceReserveTimeSec) {
		obsCountdown.value = 0
		return
	}
	const reservedAt = new Date(mediaflow.value.flowInstanceReservedAt).getTime()
	const elapsed = Math.floor((Date.now() - reservedAt) / 1000)
	const total = mediaflow.value.flowInstanceReserveTimeSec
	obsCountdown.value = Math.max(total - elapsed, 0)
	if (obsCountdown.value > 0) countdownSeenRunning = true

	// Countdown reached zero: clear the session after a short grace period. One
	// that ran out while this page watched is ended on the server too, otherwise
	// a reload shows it live again; one already expired on load may have lost its
	// instance to another session, so it is only cleared here.
	if (obsCountdown.value === 0 && mediaflow.value?.obsInstance && !obsClearTimeout) {
		const mediaflowId = mediaflow.value._id
		const endOnServer = countdownSeenRunning
		obsClearTimeout = setTimeout(() => {
			obsClearTimeout = null
			if (sesessionStateCleared.value) return
			sesessionStateCleared.value = true
			if (endOnServer) MediaflowService.stopStream(mediaflowId).catch((err) => console.error('Failed to end the expired session', err))
			store.dispatch('Mediaflows/clearObsInstance', mediaflowId)
			notify({ group: 'info', text: 'Session time limit reached. The production stopped.' })
		}, 8000)
	}
}

watch(
	() => mediaflow.value?.flowInstanceReservedAt,
	() => {
		if (obsClearTimeout) {
			clearTimeout(obsClearTimeout)
			obsClearTimeout = null
		}
		countdownSeenRunning = false
		updateObsCountdown()
		if (obsCountdownInterval) clearInterval(obsCountdownInterval)
		if (mediaflow.value?.flowInstanceReservedAt && mediaflow.value?.flowInstanceReserveTimeSec) {
			obsCountdownInterval = setInterval(updateObsCountdown, 1000)
		}
	},
	{ immediate: true }
)

watch(
	() => mediaflow.value?.obsInstance,
	(newInstance) => {
		if (!newInstance && obsClearTimeout) {
			clearTimeout(obsClearTimeout)
			obsClearTimeout = null
		}
	}
)

// A break the server already returned from: drop the local cue and re-sync
// so the Program tally follows the real on-air flow.
let cueSyncedFor = null
watch(clockNow, () => {
	const c = cue.value
	if (!c?.returnsAt) return
	const remaining = cueRemainingSec(c, clockNow.value)
	if (remaining > 0 || cueSyncedFor === c.cueId) return
	if (new Date(c.returnsAt).getTime() + 2500 > clockNow.value) return
	cueSyncedFor = c.cueId
	store.dispatch('Mediaflows/setCue', { mediaflowId: mediaflow.value._id, cue: null })
	store.dispatch('Mediaflows/getMediaflow', mediaflow.value._id).catch(() => {})
})

watch(mediaflow, () => {
	if (mediaflow.value) {
		unsubscribeMediaPulse()
		subscribeMediaPulse()
		unsubscribeMediaPulsePreview()
		subscribeMediaPulsePreview()
	}
})

onUnmounted(() => {
	detailRequestToken.value += 1
	window.removeEventListener('keydown', onGlobalKeydown)
	if (clockTimer) clearInterval(clockTimer)
	if (obsCountdownInterval) clearInterval(obsCountdownInterval)
	if (obsClearTimeout) clearTimeout(obsClearTimeout)
	scopeAlive.value = false
	unsubscribeMediaPulse()
	unsubscribeMediaPulsePreview()
})

// ---------------------------------------------------------------- ad schedules
const openScheduleEditor = async (options = {}) => {
	openSidePanel('automation')
	await nextTick()
	sidePanelRef.value?.openScheduleEditor(options)
}

const fetchAdSchedules = async (mediaflowId = mediaflow.value?._id) => {
	if (!mediaflowId) return
	adSchedulesLoading.value = true
	try {
		const schedules = await MediaflowService.getAdSchedules(mediaflowId)
		if (!scopeAlive.value || String(route.params.id) !== String(mediaflowId)) return
		adSchedules.value = schedules || []
	} catch (err) {
		if (!scopeAlive.value || String(route.params.id) !== String(mediaflowId)) return
		console.error('Failed to fetch ad schedules', err)
		notify({ group: 'error', text: err?.message || 'Failed to load automation' })
	} finally {
		if (scopeAlive.value && String(route.params.id) === String(mediaflowId)) adSchedulesLoading.value = false
	}
}

const onScheduleAdded = async () => { await fetchAdSchedules() }

const flowNameById = (id) => flows.value.find((f) => String(f._id) === String(id))?.name || ''
/** The next timed automation event, for the header button. */
const nextAutomation = computed(() => upcomingScheduledEvents(adSchedules.value, flowNameById, clockNow.value, 12 * 3_600_000, 1)[0] || null)
/** A break the server says automation started (`cue.source === 'automation'`), for the header chip. */
const automationBreak = computed(() => {
	const c = cue.value
	if (!c || c.source !== 'automation') return null
	const flow = flows.value.find((f) => String(f._id) === String(c.flowId))
	return { name: flow?.name || 'a break' }
})
const nextAutomationLabel = computed(() => {
	const ev = nextAutomation.value
	if (!ev) return ''
	const sec = Math.max(0, Math.round((ev.at - clockNow.value) / 1000))
	return sec < 45 ? 'now' : `in ${formatDurationShort(sec)}`
})

const toggleSchedule = async (schedule) => {
	if (!mediaflow.value?._id || !schedule._id || togglingScheduleId.value === schedule._id) return
	togglingScheduleId.value = schedule._id
	try {
		await MediaflowService.toggleAdSchedule(mediaflow.value._id, schedule._id)
		await fetchAdSchedules()
		notify({ group: 'success', text: `Schedule ${schedule.enabled ? 'paused' : 'activated'}` })
	} catch (err) {
		console.error('Failed to toggle schedule', err)
		notify({ group: 'error', text: err?.message || 'Failed to toggle schedule' })
	} finally {
		togglingScheduleId.value = null
	}
}


const onScheduleDelete = (schedule) => {
	scheduleToDelete.value = schedule
	showModal('confirm-delete-schedule')
}

const confirmDeleteSchedule = async () => {
	if (!scheduleToDelete.value || !mediaflow.value?._id) return
	try {
		await MediaflowService.deleteAdSchedule(mediaflow.value._id, scheduleToDelete.value._id)
		adSchedules.value = adSchedules.value.filter((s) => s._id !== scheduleToDelete.value._id)
		notify({ group: 'success', text: 'Schedule deleted' })
	} catch (err) {
		console.error('Failed to delete schedule', err)
		notify({ group: 'error', text: err?.message || 'Failed to delete schedule' })
	}
	scheduleToDelete.value = null
}

// ---------------------------------------------------------------- scenes (layouts) + breaks
const openAddFlowModal = (template = null) => addFlowModalRef.value?.open(template)

const onFlowAdded = async (newFlow, { withMedia = false } = {}) => {
	if (withMedia) await store.dispatch('Mediaflows/getMediaflow', mediaflow.value._id).catch(() => {})
	else store.dispatch('Mediaflows/addFlow', { mediaflowId: mediaflow.value._id, flow: newFlow })
	// A new scene goes straight into Preview so the next step is obvious.
	if (newFlow?._id && newFlow.kind !== 'break') desk.selectFlow(newFlow._id)
	else if (!selectedFlowId.value && layouts.value.length > 0) selectedFlowId.value = layouts.value[0]._id
}

// Clicking a scene loads it into Preview; only Send to live changes Program.
const onFlowClick = (flow) => {
	desk.selectFlow(flow._id)
}

const onFlowDelete = (flow) => {
	flowToDelete.value = flow
	showModal('confirm-delete-flow')
}

const confirmDeleteFlow = async () => {
	if (!flowToDelete.value) return
	try {
		await store.dispatch('Mediaflows/removeFlow', { mediaflowId: mediaflow.value._id, flowId: flowToDelete.value._id })
		if (selectedFlowId.value === flowToDelete.value._id) {
			selectedFlowId.value = layouts.value.length > 0 ? layouts.value[0]._id : null
		}
		notify({ group: 'success', text: flowToDelete.value.kind === 'break' ? 'Break deleted' : 'Scene deleted' })
	} catch (err) {
		console.error('Failed to delete flow', err)
		notify({ group: 'error', text: err?.message || 'Failed to delete' })
	}
	flowToDelete.value = null
}

const onFlowEdit = (flow) => {
	flowToEdit.value = flow
	editFlowModalRef.value?.toggleSidebar()
}

const onFlowUpdated = (updatedFlow) => {
	if (!updatedFlow) return
	store.dispatch('Mediaflows/setFlowName', {
		mediaflowId: mediaflow.value._id,
		flowId: updatedFlow._id,
		name: updatedFlow.data?.name ?? updatedFlow.name,
	})
	flowToEdit.value = null
}

// Duplicate: a new flow of the same kind, then every source re-added with
// its geometry (media ids are per flow, so they are cloned, not shared).
const duplicating = ref(false)
const onFlowDuplicate = async (flow) => {
	if (duplicating.value || !flow) return
	duplicating.value = true
	try {
		const extra = { kind: flow.kind === 'break' ? 'break' : 'layout' }
		if (flow.kind === 'break') extra.break = { ...flow.break }
		const copyName = `${flow.name} copy`
		const taken = takenMediaNames()
		const payloads = (Array.isArray(flow.media) ? flow.media : []).map((item) => {
			const payload = cloneMediaPayload(item, { name: uniqueMediaName(item.name, taken, copyName), show: item.show !== false })
			if (item.transform) payload.transform = { ...item.transform }
			return payload
		})
		const created = await createFlowWithMedia(mediaflow.value._id, copyName, extra, payloads)
		await store.dispatch('Mediaflows/getMediaflow', mediaflow.value._id)
		desk.selectFlow(created._id)
		notify({ group: 'success', text: `“${created.name || `${flow.name} copy`}” created in Preview` })
	} catch (err) {
		console.error('Failed to duplicate flow', err)
		notify({ group: 'error', text: flowErrorMessage(err, 'Could not duplicate') })
	} finally {
		duplicating.value = false
	}
}

// Scenes and breaks keep separate orders. Each flow's position is saved as
// `order` so it survives a reload (only the flows whose position changed are sent).
const onFlowMove = async (flow, dir) => {
	const list = [...flows.value]
	const sameKind = list.filter((f) => (f.kind === 'break') === (flow.kind === 'break'))
	const from = sameKind.findIndex((f) => f._id === flow._id)
	const to = from + dir
	if (from < 0 || to < 0 || to >= sameKind.length) return
	const a = list.indexOf(sameKind[from])
	const b = list.indexOf(sameKind[to])
	list.splice(a, 1, sameKind[to])
	list.splice(b, 1, sameKind[from])
	const mediaflowId = mediaflow.value._id
	const ordered = list.map((f, i) => ({ ...f, order: i }))
	const changed = ordered.filter((f) => list[f.order].order !== f.order)
	store.dispatch('Mediaflows/setFlows', { mediaflowId, flows: ordered })
	try {
		await Promise.all(changed.map((f) => MediaflowService.updateFlow(mediaflowId, f._id, { order: f.order })))
		desk.post({ type: 'refresh' })
	} catch (err) {
		console.error('Failed to save the scene order', err)
		notify({ group: 'error', text: 'Could not save the new order' })
		void store.dispatch('Mediaflows/getMediaflow', mediaflowId)
	}
}

// ---------------------------------------------------------------- graphics (overlays)
const graphicSettingsOpen = ref(false)
const graphicSettingsTarget = ref(null)
const graphicSettingsSaving = ref(false)
const graphicSettingsError = ref('')
const overlayToDelete = ref(null)
const rowNames = computed(() => overlayRows(overlays.value).map((r) => r.name))

const openAddGraphic = (rowOrTemplate) => {
	if (!capabilities.value.overlays || !enabled.value) return
	const options = {}
	if (typeof rowOrTemplate === 'string') options.row = rowOrTemplate
	else if (rowOrTemplate && typeof rowOrTemplate === 'object') { options.template = rowOrTemplate; options.row = rowOrTemplate.row }
	addGraphicModalRef.value?.toggleSidebar(options)
}
const onOverlayAdded = (overlay) => {
	// The modal already POSTed; reflect it, or re-sync when the shape is unexpected.
	if (overlay?._id) store.dispatch('Mediaflows/addOverlay', { mediaflowId: mediaflow.value._id, overlay })
	else void store.dispatch('Mediaflows/getMediaflow', mediaflow.value._id)
	desk.post({ type: 'refresh' })
}
const onToggleOverlay = (overlay) => { void desk.takeOverlay(overlay) }
const onEditOverlay = (overlay) => {
	desk.selectOverlay(overlay._id)
	if (!canEditCanvas.value) notify({ group: 'info', text: 'Position graphics on a larger screen.' })
	else notify({ group: 'info', text: `Drag ${overlay.name} on Preview to place it` })
}
const onOverlaySettings = (overlay) => {
	graphicSettingsTarget.value = overlay
	graphicSettingsError.value = ''
	graphicSettingsOpen.value = true
}
const saveOverlaySettings = async (data) => {
	const overlay = graphicSettingsTarget.value
	if (!overlay) return
	graphicSettingsSaving.value = true
	graphicSettingsError.value = ''
	try {
		await store.dispatch('Mediaflows/updateOverlay', { mediaflowId: mediaflow.value._id, overlayId: overlay._id, data })
		graphicSettingsOpen.value = false
		notify({ group: 'success', text: 'Graphic updated' })
	} catch (err) {
		console.error('Failed to update overlay', err)
		graphicSettingsError.value = err?.message || 'Could not save the graphic'
	} finally {
		graphicSettingsSaving.value = false
	}
}
const onDeleteOverlay = (overlay) => {
	overlayToDelete.value = overlay
	showModal('confirm-delete-overlay')
}
const confirmDeleteOverlay = async () => {
	const overlay = overlayToDelete.value
	if (!overlay) return
	try {
		await store.dispatch('Mediaflows/deleteOverlay', { mediaflowId: mediaflow.value._id, overlayId: overlay._id })
		if (desk.editingOverlayId.value === overlay._id) desk.selectMedia(null)
		notify({ group: 'success', text: 'Graphic deleted' })
	} catch (err) {
		console.error('Failed to delete overlay', err)
		notify({ group: 'error', text: err?.message || 'Could not delete the graphic' })
	}
	overlayToDelete.value = null
}
/** Global order = groups top → bottom, items top → bottom (index 0 = top-most layer). */
const persistOverlayOrder = async (rows) => {
	const ids = rows.flatMap((r) => r.items.map((o) => o._id))
	try {
		await store.dispatch('Mediaflows/reorderOverlays', { mediaflowId: mediaflow.value._id, ids })
	} catch (err) {
		console.error('Failed to reorder overlays', err)
		notify({ group: 'error', text: 'Could not save the new order' })
		void store.dispatch('Mediaflows/getMediaflow', mediaflow.value._id)
	}
}
const onMoveOverlay = (overlay, dir) => {
	const rows = overlayRows(overlays.value).map((r) => ({ ...r, items: [...r.items] }))
	const row = rows.find((r) => r.items.some((o) => o._id === overlay._id))
	if (!row) return
	const from = row.items.findIndex((o) => o._id === overlay._id)
	const to = from + dir
	if (to < 0 || to >= row.items.length) return
	row.items.splice(to, 0, row.items.splice(from, 1)[0])
	void persistOverlayOrder(rows)
}

// ---------------------------------------------------------------- breaks
const breakModalOpen = ref(false)
const breakModalTarget = ref(null)
const breakModalSaving = ref(false)
const breakModalError = ref('')
const breakModalTemplate = ref('')
// Empty = the break modal reopens on the last template used.
const openAddBreak = (template = '') => {
	breakModalTarget.value = null
	breakModalTemplate.value = typeof template === 'string' ? template : ''
	breakModalError.value = ''
	breakModalOpen.value = true
}
const onEditBreak = (flow) => { breakModalTarget.value = flow; breakModalError.value = ''; breakModalOpen.value = true }
const saveBreak = async ({ name, break: settings, template, lBandInputKey, imageFile }) => {
	breakModalSaving.value = true
	breakModalError.value = ''
	try {
		if (breakModalTarget.value) {
			const flowId = breakModalTarget.value._id
			await MediaflowService.updateFlow(mediaflow.value._id, flowId, { break: settings })
			store.dispatch('Mediaflows/setFlowFields', { mediaflowId: mediaflow.value._id, flowId, fields: { break: settings } })
			notify({ group: 'success', text: 'Break updated' })
		} else {
			const taken = takenMediaNames()
			// Top layer first: the squeezed program sits over the L-band frame.
			const payloads = []
			if (template === 'l-band') {
				const input = liveInputs.value.find((i) => i.key === lBandInputKey) || liveInputs.value[0]
				if (input) {
					payloads.push(cloneMediaPayload(input.media, {
						name: uniqueMediaName(input.media.name, taken, name),
						show: true,
						transform: lBandProgramTransform(canvas.value),
					}))
				}
			}
			const buildPayloads = async (createdFlow) => {
				if (!imageFile) return payloads
				const fdata = new FormData()
				fdata.append('file', imageFile)
				const res = await MediaflowService.uploadFlowImage(mediaflow.value._id, createdFlow._id, fdata)
				if (!res?.success || !res.imageId) throw new Error('Could not upload the image. The break was not created.')
				return [...payloads, {
					name: uniqueMediaName(`${name} ${template === 'l-band' ? 'frame' : 'image'}`, taken),
					type: 'image',
					imageId: res.imageId,
					imageUrl: res.imageUrl,
					show: true,
				}]
			}
			const created = await createFlowWithMedia(mediaflow.value._id, name, { kind: 'break', break: settings }, buildPayloads)
			const flow = { ...created, kind: 'break', break: created.break || settings }
			await store.dispatch('Mediaflows/getMediaflow', mediaflow.value._id)
			desk.selectFlow(flow._id)
			let text = `“${name}” is in Preview.`
			if (!imageFile) text += template === 'l-band' ? ' Add the frame image in Sources.' : ' Add the ad video or image in Sources.'
			notify({ group: 'success', text })
		}
		breakModalOpen.value = false
	} catch (err) {
		console.error('Failed to save break', err)
		breakModalError.value = flowErrorMessage(err, 'Could not save the break')
	} finally {
		breakModalSaving.value = false
	}
}
const onCueBreak = (flow) => { void desk.cueBreak(flow) }
const onEndBreak = () => { void desk.endBreak() }
const scheduleLimitMessage = computed(() => (scheduleLimitReached(adSchedules.value.length, Number(maxMediaflowAdSchedule.value) || 0)
	? scheduleLimitReason(adSchedules.value.length, Number(maxMediaflowAdSchedule.value) || 0)
	: ''))
const onScheduleBreak = (flow) => {
	if (scheduleLimitMessage.value) {
		notify({ group: 'error', text: scheduleLimitMessage.value })
		openSidePanel('automation')
		return
	}
	void openScheduleEditor({ breakPrefill: { flow, returnFlowId: desk.onAirFlow.value?._id || null } })
}
const onEditBreakLayout = (flow) => {
	desk.selectFlow(flow._id)
}

// ---------------------------------------------------------------- output / preview modals
// Destination is picked inside the Settings panel.
const openDestinationModal = async () => {
	openSidePanel('settings')
	await nextTick()
	sidePanelRef.value?.openDestinationPicker()
}

const openMediaPlayerModal = async (media) => {
	showMediaPlayerModal.value = true
	openMediaPlayerSelected.value = media
	mediaPlayerStream.value = null
	mediaPlayerMediaPulse.value = {}
	mediaPlayerUrl.value = ''

	if (media.type === 'live') {
		const stream = allInOneStreams.value.find((s) => s._id === media.stream)
		if (stream) {
			mediaPlayerLoading.value = true
			try {
				const pulseObject = await StreamStatsService.getStreamMediaPulse(stream.key)
				mediaPlayerMediaPulse.value = pulseObject || {}
				mediaPlayerStream.value = stream
				mediaPlayerUrl.value = `https://player.castr.com/${stream.key}`
			} catch (e) {
				console.error('Failed to fetch mediaPulse for stream', e)
				mediaPlayerMediaPulse.value = {}
			} finally {
				mediaPlayerLoading.value = false
			}
		}
	}

	if (media.type === 'vod') {
		mediaPlayerUrl.value = `https://player.castr.com/vod/${media.file.replace('.mp4', '')}?preview=true`
		if (media.embedUrl && media.embedUrl.includes('range=')) {
			mediaPlayerUrl.value = media.embedUrl + '&preview=true'
		}
	}
}

const closeMediaPlayerModal = () => {
	showMediaPlayerModal.value = false
	openMediaPlayerSelected.value = { name: '' }
	mediaPlayerStream.value = null
	mediaPlayerMediaPulse.value = {}
}

// ---------------------------------------------------------------- sources
const openAddSourceModal = () => {
	if (!selectedFlow.value) return
	addFlowMediaModalRef.value?.toggleSidebar()
}

const onMediaAdded = (mediaList) => {
	if (!selectedFlow.value) return
	const flowId = selectedFlow.value._id
	store.dispatch('Mediaflows/clearFlowMedia', { mediaflowId: mediaflow.value._id, flowId })
	const list = Array.isArray(mediaList) ? mediaList : [mediaList]
	list.forEach((media) => {
		if (typeof media.volume === 'undefined') media.volume = 100
		if (typeof media.show === 'undefined') media.show = false
		if (typeof media.muted === 'undefined') media.muted = false
		if (typeof media.audioOffset === 'undefined') media.audioOffset = 0
		store.dispatch('Mediaflows/addFlowMedia', { mediaflowId: mediaflow.value._id, flowId, media })
	})
}

const draftHint = () => (sessionActive.value ? 'Apply to live to put it on air.' : 'Apply to Program to use it.')
const onMediaStaged = (payload) => {
	if (!selectedFlow.value) return
	desk.stageAdd(payload)
	notify({ group: 'info', text: `${payload?.name || 'Source'} added to Preview. ${draftHint()}` })
}
const onMediaEditStaged = (mediaId, patch) => {
	if (!selectedFlow.value) return
	desk.stageEdit(mediaId, patch)
	notify({ group: 'info', text: `Change is in Preview. ${draftHint()}` })
}
const sourceActions = useMediaflowSourceActions({ mediaflow, store, notify, desk })
const onMediaToggle = sourceActions.toggleShow
const onSourcesReorder = sourceActions.reorder
const onVolumeChange = sourceActions.setVolume
const toggleMuteVolume = sourceActions.toggleMute
const onAudioSyncChange = sourceActions.setAudioSync

/** Sources dock → "Position & size": select it on the canvas for dragging / fine-tune. */
const onMediaPosition = (media) => desk.selectMedia(media.id)

const onMediaEdit = async (media) => {
	slectedFlowMediaSource.value = media
	await nextTick()
	editMediaflowModalRef.value?.toggleSidebar()
}

const onMediaUpdated = (updatedMedia) => {
	if (!selectedFlow.value || !updatedMedia) return
	store.dispatch('Mediaflows/updateFlowMedia', {
		mediaflowId: mediaflow.value._id,
		flowId: selectedFlow.value._id,
		mediaId: updatedMedia.id,
		media: updatedMedia,
	})
}

const onMediaDelete = (media) => {
	if (!selectedFlow.value) return
	mediaToDelete.value = { flowId: selectedFlow.value._id, mediaId: media.id, name: media.name, staged: desk.stagesEdits.value }
	showModal('modal-delete-media')
}

const confirmMediaDelete = async () => {
	const { flowId, mediaId } = mediaToDelete.value
	if (desk.stagesEdits.value && flowId === desk.editingFlowId.value) {
		desk.stageRemove(mediaId)
		return
	}
	try {
		await store.dispatch('Mediaflows/deleteFlowMedia', { mediaflowId: mediaflow.value._id, flowId, mediaId })
		notify({ group: 'success', text: 'Source removed from the scene' })
	} catch (err) {
		console.error('Failed to delete media item', err)
		notify({ group: 'error', text: 'Failed to delete media' })
	}
}

const mediaPreviewAvailable = (media) => {
	if (media.type === 'live') return true
	if (media.type === 'vod' && (media?.file ?? '').includes('mp4')) return true
	if (media.embedUrl) return true
	return false
}

// ---------------------------------------------------------------- loading
const initializeDetailState = () => {
	destination.value = mediaflow.value?.destination ?? null
	const current = layouts.value.find((flow) => flow._id === selectedFlowId.value)
	// The last scene previewed here, else the one on Program (OBS studio mode).
	selectedFlowId.value = current?._id || desk.restorePreviewId()
}

const loadMediaflow = async (id) => {
	const token = ++detailRequestToken.value
	const isCurrentRequest = () => (
		scopeAlive.value
		&& token === detailRequestToken.value
		&& String(route.params.id) === String(id)
	)
	let seedSnapshot = null
	let rejoinAnnounced = false

	// Opening onto a session that is already running on the server: this tab
	// is just (re)connecting to it, nothing restarts.
	const announceRejoin = () => {
		if (rejoinAnnounced || !isCurrentRequest() || !mediaflow.value?.obsInstance) return
		rejoinAnnounced = true
		notify({ group: 'info', text: 'Reconnected. Your production is still live.' })
	}

	detailResolving.value = true
	detailSyncing.value = true
	adSchedules.value = []
	selectedFlowId.value = null
	store.dispatch('Mediaflows/setTeamPermissionError', { error: false })

	const seedFromList = async () => {
		if (!isCurrentRequest() || mediaflowReady.value) return false
		const seeded = await store.dispatch('Mediaflows/seedMediaflow', id)
		if (!seeded || !isCurrentRequest()) return false
		initializeDetailState()
		seedSnapshot = JSON.stringify(mediaflow.value)
		detailResolving.value = false
		announceRejoin()
		void fetchAdSchedules(id)
		return true
	}

	await seedFromList()

	void store.dispatch('Streams/getStreams').catch((err) => {
		if (isCurrentRequest()) console.error('Failed to load streams', err)
	}).finally(() => { streamsReady.value = true })
	void store.dispatch('Mediaflows/getMediaflows').then(() => seedFromList()).catch((err) => {
		if (isCurrentRequest()) console.error('Failed to synchronize Cloud Productions', err)
	})

	try {
		const freshMediaflow = await store.dispatch('Mediaflows/fetchMediaflow', id)
		if (!isCurrentRequest()) return
		const seededStateUnchanged = seedSnapshot !== null
			&& JSON.stringify(mediaflow.value) === seedSnapshot
		if (seedSnapshot === null || seededStateUnchanged) {
			await store.dispatch('Mediaflows/applyMediaflow', freshMediaflow)
			if (!isCurrentRequest()) return
			initializeDetailState()
			void fetchAdSchedules(id)
		}
		detailResolving.value = false
		announceRejoin()
	} catch (err) {
		if (!isCurrentRequest()) return
		console.error('Failed to load mediaflow data', err)
		if (err && err.toString().includes('not have access to')) {
			store.dispatch('Mediaflows/setTeamPermissionError', { error: true })
		}
		notify({ group: 'error', text: 'Cloud Production load failed' })
		await router.push({ name: 'MediaflowDashboard' })
	} finally {
		if (isCurrentRequest()) detailSyncing.value = false
	}
}

onMounted(() => {
	clockTimer = setInterval(() => { clockNow.value = Date.now() }, 1000)
	window.addEventListener('keydown', onGlobalKeydown)
	void store.dispatch('User/loadUser').catch(() => {}).finally(() => { planSettled.value = true })
	void loadMediaflow(route.params.id)
})

watch(
	() => route.params.id,
	(id, previousId) => {
		if (previousId !== undefined && String(id) !== String(previousId)) void loadMediaflow(id)
	}
)
</script>

<template>
	<div class="production-page container detail-shell px-4" :data-detail-syncing="detailSyncing || undefined">
		<!-- Loading: the control room's shape, never a gate, until plan + production are known -->
		<div v-if="!ready" class="flex flex-col gap-3 pb-6" aria-busy="true" aria-label="Opening your production" data-testid="control-room-skeleton">
			<div class="mt-3 flex h-12 min-w-0 items-center gap-3 md:mt-4">
				<span class="size-8 shrink-0 rounded-md bg-surface-2" />
				<span v-if="skeletonName" class="min-w-0 truncate text-lg font-semibold text-white" data-testid="skeleton-name">{{ skeletonName }}</span>
				<span v-else class="h-5 w-48 animate-pulse rounded bg-surface-2" />
				<span class="h-6 w-16 shrink-0 animate-pulse rounded-full bg-surface-2" />
				<span class="ml-auto hidden h-8 w-24 animate-pulse rounded-md bg-surface-2 md:block" />
			</div>
			<div class="grid grid-cols-1 gap-x-3 gap-y-2 lg:grid-cols-[minmax(0,1fr)_104px_minmax(0,1fr)]">
				<div class="flex min-w-0 flex-col gap-2">
					<span class="h-8" />
					<div class="aspect-video w-full animate-pulse rounded-xl bg-surface-2" />
				</div>
				<div class="mt-10 hidden animate-pulse rounded-xl bg-surface-2 lg:block" />
				<div class="hidden min-w-0 flex-col gap-2 md:flex">
					<span class="h-8" />
					<div class="aspect-video w-full animate-pulse rounded-xl bg-surface-2" />
				</div>
			</div>
			<div class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
				<div v-for="i in 4" :key="i" class="h-[220px] animate-pulse rounded-xl bg-surface-2" />
			</div>
		</div>

		<main v-else-if="(!hasLiveSubscription || isLiveSubscriptionExpired || maxMediaflows === 0)">
			<EmptyState title="Cloud Production is not available" subtitle="Upgrade your plan to produce and switch live sources in the cloud.">
				<template #illustration>
					<IconMediaflowLockedPlaceholderImage class="mx-auto" />
				</template>
				<template #actions>
					<FormButton type="main" to="/subscribe">Upgrade plan</FormButton>
				</template>
			</EmptyState>
		</main>

		<main v-else-if="!mediaflowTrial">
			<EmptyState title="Access restricted" subtitle="Cloud Production isn't available on your current plan.">
				<template #icon>
					<icon-base width="28" height="28" fill="none"><icon-lock /></icon-base>
				</template>
			</EmptyState>
		</main>

		<main v-else class="control-room flex flex-col gap-3 pb-24 lg:pb-6" data-testid="control-room">
			<PageHeader :title="mediaflow.name" mobile-inline show-meta-on-mobile class="!mb-0 !mt-3 md:!mt-4 md:!pb-3" data-testid="production-header">
				<template #leading>
					<BackButton to="/cloud-production" title="Back to Cloud Production" />
				</template>
				<template #title>
					<Title
						:editable="enabled"
						:title="mediaflow.name"
						title-classes="m-0 min-w-0 rounded-md text-lg font-semibold text-white"
						edit-class="bg-surface-3 line-clamp-none"
						max-width="max-w-full lg:max-w-[440px]"
						data-testid="production-title"
						@changed="onNameChange"
					/>
				</template>
				<template #meta>
					<StatusPill pill :variant="statusPill.variant" :pulse="statusPill.pulse" role="status" :data-status="productionStatus" data-testid="production-status">
						{{ statusPill.label }}
					</StatusPill>
					<MediaflowSessionChip :session="session" :status="productionStatus" />
					<button
						v-if="automationBreak"
						type="button"
						class="hidden h-6 min-w-0 max-w-[260px] items-center gap-1.5 rounded-full bg-error-transparent px-2.5 text-xs font-medium text-error-light ring-1 ring-error md:inline-flex"
						:title="`Automation is running ${automationBreak.name}`"
						data-testid="automation-running"
						@click="openSidePanel('automation')"
					>
						<span class="size-1.5 shrink-0 rounded-full bg-error-light animate-pulse" aria-hidden="true" />
						<span class="truncate">Automation · {{ automationBreak.name }}</span>
					</button>
					<button
						v-else-if="nextAutomation"
						type="button"
						class="hidden h-6 min-w-0 max-w-[260px] items-center gap-1.5 rounded-full bg-surface-3 px-2.5 text-xs text-surface-8 ring-1 ring-surface-4 hover:text-white md:inline-flex"
						:title="`${nextAutomation.action} at ${nextAutomation.atLabel}${sessionActive ? '' : ' (runs once live)'}`"
						data-testid="automation-up-next"
						@click="openSidePanel('automation')"
					>
						<icon-base class="shrink-0" width="12" height="12" :width-view-box="24" :height-view-box="24" fill="none"><icon-calendar-outline /></icon-base>
						<span class="truncate">Next: {{ nextAutomation.action }}</span>
						<span class="shrink-0 font-semibold tabular-nums text-surface-text-1">{{ nextAutomationLabel }}</span>
					</button>
				</template>

				<!-- Output = the linked stream, as one chip -->
				<span class="hidden min-w-0 items-center gap-1.5 lg:inline-flex" data-testid="output-chip">
					<span class="hidden text-xs text-surface-8 2xl:inline">Output</span>
					<icon-base class="hidden shrink-0 text-surface-7 2xl:block" width="12" height="12" :width-view-box="24" :height-view-box="24" fill="none"><icon-arrow-right /></icon-base>
					<a
						v-if="destinationStream"
						:href="streamPageUrl"
						target="_blank"
						rel="noopener"
						class="inline-flex min-w-0 max-w-[150px] items-center gap-1.5 rounded-full bg-surface-3 px-2.5 py-1 2xl:max-w-[200px] text-xs font-medium text-surface-text-2 ring-1 ring-surface-4 hover:text-white hover:ring-surface-6"
						:title="`Open ${destinationStream.name} in a new tab`"
						data-testid="output-stream-link"
					>
						<span class="truncate">{{ destinationStream.name }}</span>
						<icon-base class="shrink-0 text-surface-8" width="11" height="11"><icon-open-external /></icon-base>
					</a>
					<span v-else-if="destinationPending" class="inline-block h-6 w-28 animate-pulse rounded-full bg-surface-3" aria-label="Loading destination" />
					<span v-else class="inline-flex items-center rounded-full bg-surface-3 px-2.5 py-1 text-xs text-surface-8 ring-1 ring-surface-4" data-testid="output-stream-none">No destination yet</span>
					<Tooltip :content="outputLocked ? 'Locked while live' : destinationStream ? 'Change destination' : 'Choose destination'" wrapper-class="inline-flex" wrap-trigger>
						<FormButton type="link" size="sm" class="!px-1 text-xs text-surface-8 hover:text-white" :disabled="!enabled || outputLocked" :aria-label="destinationStream ? 'Change destination' : 'Choose destination'" data-testid="output-change" @click="openDestinationModal">
							<icon-base v-if="outputLocked" width="12" height="12" fill="none"><icon-lock /></icon-base>
							<icon-base v-else width="13" height="13" class="2xl:hidden"><icon-edit /></icon-base>
							<span class="hidden 2xl:inline" :class="outputLocked ? 'ml-1' : ''">{{ destinationStream ? 'Change' : 'Choose' }}</span>
						</FormButton>
					</Tooltip>
				</span>

				<span class="hidden w-1 md:block" aria-hidden="true" />
				<FormButton type="secondary" size="md" class="hidden gap-2 md:inline-flex max-xl:!w-8 max-xl:!px-0 max-xl:justify-center" aria-label="Automation" title="Automation" data-testid="open-automation" @click="openSidePanel('automation')">
					<icon-base width="14" height="14" :width-view-box="24" :height-view-box="24" fill="none"><icon-calendar-outline /></icon-base>
					<span class="hidden xl:inline">Automation</span>
				</FormButton>
				<Tooltip content="Output encoder and destination" wrapper-class="hidden md:inline-flex">
					<FormButton type="secondary" size="md" class="gap-2 max-xl:!w-8 max-xl:!px-0 max-xl:justify-center" :disabled="!enabled" aria-label="Settings" data-testid="open-settings" @click="openSidePanel('settings')">
						<icon-base width="15" height="15" :width-view-box="20" :height-view-box="20" fill="none"><icon-cog /></icon-base>
						<span class="hidden xl:inline">Settings</span>
					</FormButton>
				</Tooltip>

				<!-- The one filled primary in the header -->
				<template v-if="!sessionActive && enabled">
					<Tooltip v-if="startBlockedReason" wrap-trigger :content="startBlockedReason" class="hidden lg:inline-flex">
						<FormButton type="main" size="md" :disabled="true" class="gap-2 !px-4 font-semibold">Go live</FormButton>
					</Tooltip>
					<!-- Offline and ready: a slow rotating ring draws the eye to Go live. -->
					<span v-else class="mf-attention hidden lg:inline-flex" :class="{ 'mf-attention--on': goLiveAttention }" data-testid="go-live-attention">
						<FormButton
							type="main"
							size="md"
							class="gap-2 !px-4 font-semibold"
							:is-loading="startStreamProcessing"
							data-testid="go-live"
							@click="confirmGoLive"
						>
							Go live
						</FormButton>
					</span>
				</template>
				<FormButton
					v-else-if="sessionActive"
					type="red"
					size="md"
					class="hidden gap-2 !px-4 font-semibold lg:inline-flex"
					:is-loading="startStreamProcessing"
					:disabled="!enabled"
					data-testid="stop-production"
					@click="showStopStreamModal"
				>
					Stop
				</FormButton>

				<DropDownMenu ref="actionsMenu" type="secondary" size="md" is-icon position="right" aria-label="More actions" list-classes="min-w-[220px]">
					<template #toggle-button>
						<icon-base><icon-elipsis-vertical /></icon-base>
					</template>
					<template #menu-items>
						<DropDownMenuItem class="hidden md:block" @click="runMenuAction(openPopout)">Open control window</DropDownMenuItem>
						<DropDownMenuItem class="md:hidden" @click="runMenuAction(() => openSidePanel('automation'))">Automation</DropDownMenuItem>
						<DropDownMenuItem class="md:hidden" @click="runMenuAction(() => openSidePanel('settings'))">Settings</DropDownMenuItem>
						<DropDownMenuItem class="md:hidden" :disabled="!enabled || outputLocked" @click="runMenuAction(openDestinationModal)">{{ destinationStream ? 'Change destination' : 'Choose destination' }}</DropDownMenuItem>
						<DropDownMenuItem v-if="destinationStream" @click="runMenuAction(onStreamPage)">Open destination stream</DropDownMenuItem>
						<DropDownMenuItem @click="runMenuAction(() => modalDEMOVideo?.show())">Watch tutorial</DropDownMenuItem>
						<DropDownMenuItem @click="runMenuAction(openHelp)">How Cloud Production works</DropDownMenuItem>
						<DropDownMenuItem v-if="enabled" :disabled="!!mediaPulsePreview?.alive" @click="runMenuAction(toggleMediaflow)">Disable production</DropDownMenuItem>
						<DropDownMenuItem
							:disabled="!!mediaPulsePreview?.alive"
							custom-classes="h-10 md:h-8 !p-3 md:!p-2 hover:bg-surface-3 text-error-light hover:text-error-light text-nowrap leading-none text-base md:text-tiny flex items-center w-full truncate disabled:opacity-50"
							@click="runMenuAction(onDeleteMediaflowRequest)"
						>
							Delete production
						</DropDownMenuItem>
					</template>
				</DropDownMenu>
			</PageHeader>


			<!-- Disabled: the single Enable CTA -->
			<Alert v-if="!enabled" type="normal" role="status" data-testid="disabled-notice">
				<span class="flex w-full flex-wrap items-center gap-x-3 gap-y-2">
					<span class="inline-flex items-center gap-2">
						<icon-base width="14" height="14" fill="none" class="shrink-0"><icon-lock /></icon-base>
						This production is disabled.
					</span>
					<FormButton type="main" size="sm" class="ml-auto" data-testid="enable-production" @click="toggleMediaflow">Enable production</FormButton>
				</span>
			</Alert>

			<!-- Empty production: no workspace until the first scene exists -->
			<section v-if="!layouts.length" class="rounded-xl bg-surface-2 ring-1 ring-surface-3" data-testid="empty-production">
				<EmptyState title="No scenes yet" subtitle="Add a scene, put cameras, videos or web pages in it, then send it live." min-height="320px">
					<template #icon>
						<icon-base width="26" height="26" :width-view-box="24" :height-view-box="24" fill="none"><icon-stacked-layers /></icon-base>
					</template>
					<template #actions>
						<FormButton type="main" size="md" :disabled="!enabled" data-testid="create-first-flow" @click="openAddFlowModal()">New scene</FormButton>
						<FormButton type="link" size="sm" @click="modalDEMOVideo?.show()">Watch a tutorial</FormButton>
					</template>
				</EmptyState>
			</section>

			<!-- The control room: monitors on top, OBS docks underneath -->
			<div
				v-else
				class="flex min-w-0 flex-col gap-3"
				:class="!enabled ? 'pointer-events-none opacity-70' : ''"
				:inert="!enabled || undefined"
				data-testid="control-room-body"
			>
				<!--
					Monitors grid (lg+): rows = header · video · strip · extras, columns =
					Preview · transition rail · Program. Both monitors drop their parts
					into the same rows, so headers, video tops and strips line up.
				-->
				<section
					class="grid min-w-0 grid-cols-1 gap-x-3 gap-y-2 lg:grid-cols-[minmax(0,1fr)_104px_minmax(0,1fr)] lg:grid-rows-[auto_auto_auto_auto]"
					aria-label="Monitors"
					data-testid="monitors"
				>
					<MediaflowEditorPanel
						v-if="canEditCanvas"
						:desk="desk"
						:canvas="canvas"
						:streams="allInOneStreams"
						:session-active="sessionActive"
						:enabled="enabled"
						:interactive="canEditCanvas"
						:portrait="isPortraitResolution"
						:send-disabled-reason="sendDisabledReason"
						:loading="detailSyncing && !selectedFlow"
						@popout="openPopout"
						@send="onSendToLive()"
					/>
					<MediaflowTransitionRail
						v-if="canEditCanvas"
						class="lg:col-start-2 lg:row-start-2"
						:desk="desk"
						:session-active="sessionActive"
						:disabled-reason="sendDisabledReason"
						:enabled="enabled"
						@send="onSendToLive()"
					/>
					<div v-if="canEditCanvas" class="hidden items-center justify-center lg:col-start-2 lg:row-start-3 lg:flex">
						<MediaflowShortcuts />
					</div>
					<MediaflowMonitor
						label="Program"
						:mediaflow="mediaflow"
						:destination-stream="destinationStream"
						:destination-pending="destinationPending"
						:media-pulse="mediaPulsePreview"
						:portrait="isPortraitResolution"
						:session-active="sessionActive"
						:health="programHealth"
						:on-air-flow="desk.onAirFlow.value"
						:cue="cue"
						:now="clockNow"
						:flows="flows"
						:ending="desk.cueing.value"
						:canvas="canvas"
						:streams="allInOneStreams"
						:overlays="overlays"
						@end-break="onEndBreak"
					/>
				</section>

				<!-- Docks (OBS): Scenes · Sources · Graphics · Breaks, equal height -->
				<section
					class="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 lg:h-[clamp(260px,calc(100vh-600px),420px)] lg:grid-cols-4"
					aria-label="Docks"
					data-testid="docks"
				>
					<MediaflowScenesDock
						class="md:h-[280px] lg:h-full"
						:desk="desk"
						:enabled="enabled"
						:session-active="sessionActive"
						:max-flows="maxMediaflowFlows"
						:flow-count="flows.length"
						:switching-flow-id="switchingFlowId"
						:mobile="isMobile"
						@select="onFlowClick"
						@send-to-live="onSceneSend"
						@add="openAddFlowModal"
						@rename="onFlowEdit"
						@duplicate="onFlowDuplicate"
						@delete="onFlowDelete"
						@move="onFlowMove"
					/>
					<MediaflowSourcesDock
						v-if="canEditCanvas"
						class="md:h-[280px] lg:h-full"
						:flow="desk.previewFlow.value"
						:draft-state-for="desk.mediaDraftState"
						:audio-live="desk.editingIsOnAir.value"
						:max-media="maxMediaflowMedia"
						:enabled="enabled"
						:subtitle-for="sourceSubtitle"
						:preview-available="mediaPreviewAvailable"
						:selected-media-id="desk.selectedMediaId.value"
						@select="desk.selectMedia($event.id)"
						@position="onMediaPosition"
						@add="openAddSourceModal"
						@reorder="onSourcesReorder"
						@toggle-show="onMediaToggle"
						@toggle-mute="toggleMuteVolume"
						@volume="onVolumeChange"
						@audio-sync="onAudioSyncChange"
						@preview="openMediaPlayerModal"
						@edit="onMediaEdit"
						@delete="onMediaDelete"
					/>
					<MediaflowGraphicsDock
						class="md:h-[280px] lg:h-full"
						:desk="desk"
						:canvas="canvas"
						:enabled="enabled"
						:session-active="sessionActive"
						:mobile="isMobile"
						@toggle="onToggleOverlay"
						@position="onEditOverlay"
						@settings="onOverlaySettings"
						@delete="onDeleteOverlay"
						@move="onMoveOverlay"
						@add="openAddGraphic"
					/>
					<MediaflowBreaksDock
						class="md:h-[280px] lg:h-full"
						:desk="desk"
						:canvas="canvas"
						:enabled="enabled"
						:session-active="sessionActive"
						:now="clockNow"
						:max-flows="maxMediaflowFlows"
						:flow-count="flows.length"
						:mobile="isMobile"
						:schedule-disabled-reason="scheduleLimitMessage"
						@cue="onCueBreak"
						@end="onEndBreak"
						@settings="onEditBreak"
						@schedule="onScheduleBreak"
						@edit-layout="onEditBreakLayout"
						@rename="onFlowEdit"
						@delete="onFlowDelete"
						@add="openAddBreak"
					/>
				</section>
			</div>
		</main>

		<!-- Mobile action bar -->
		<div
			v-if="ready && mediaflowTrial && hasLiveSubscription && !isLiveSubscriptionExpired"
			class="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 bg-surface-1/95 shadow-[0_-8px_24px_rgba(0,0,0,0.25)] px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur lg:hidden"
			data-testid="mobile-action-bar"
		>
			<!-- Floats on top of the bar (out of flow) so a draft never pushes content. -->
			<div
				v-if="!canEditCanvas && desk.pendingCount.value > 0"
				class="mf-mobile-draft absolute inset-x-3 bottom-full mb-2 flex h-10 items-center gap-2 rounded-lg px-3 ring-1"
				role="status"
				data-testid="mobile-draft-bar"
			>
				<span class="size-2 shrink-0 rounded-full bg-gold" aria-hidden="true" />
				<span class="min-w-0 flex-1 truncate text-xs font-medium text-gold">{{ desk.pendingCount.value }} unsaved {{ desk.pendingCount.value === 1 ? 'change' : 'changes' }}</span>
				<FormButton type="link" size="sm" class="text-xs" :disabled="desk.applying.value" @click="desk.discardDraft()">Discard</FormButton>
				<FormButton type="main" size="sm" class="!px-2.5 text-xs" :is-loading="desk.applying.value" @click="desk.applyDraft()">{{ draftApplyLabel(sessionActive) }}</FormButton>
			</div>
			<StatusPill pill :variant="statusPill.variant" :pulse="statusPill.pulse" class="shrink-0">{{ statusPill.label }}</StatusPill>
			<span class="min-w-0 flex-1 truncate text-xs text-surface-8">
				<template v-if="cue">Break running</template>
				<template v-else-if="desk.onAirFlow.value">{{ sessionActive ? 'On air' : 'Starts with' }}: {{ desk.onAirFlow.value.name }}</template>
			</span>
			<FormButton v-if="cue" type="red" size="md" class="font-semibold" :is-loading="desk.cueing.value" data-testid="mobile-end-break" @click="onEndBreak">End break</FormButton>
			<FormButton v-else-if="sessionActive" type="red" size="md" class="font-semibold" :is-loading="startStreamProcessing" :disabled="!enabled" data-testid="mobile-stop" @click="showStopStreamModal">Stop</FormButton>
			<span v-else-if="enabled" class="mf-attention inline-flex" :class="{ 'mf-attention--on': goLiveAttention }">
				<FormButton type="main" size="md" class="font-semibold" :disabled="!!startBlockedReason" :title="startBlockedReason || undefined" :is-loading="startStreamProcessing" data-testid="mobile-go-live" @click="confirmGoLive">Go live</FormButton>
			</span>
		</div>

		<MediaflowSidePanel
			v-if="mediaflowReady && mediaflow?._id"
			ref="sidePanelRef"
			v-model="sidePanelOpen"
			v-model:tab="sidePanelTab"
			:mediaflow-id="mediaflow._id"
			:schedules="adSchedules"
			:flows="flows"
			:loading="adSchedulesLoading"
			:max-schedules="maxMediaflowAdSchedule"
			:toggling-id="togglingScheduleId"
			:locked="outputLocked"
			:enabled="enabled"
			:session-active="sessionActive"
			:now="clockNow"
			:mediaflow="mediaflow"
			:destination-stream="destinationStream"
			:disabled-stream-ids="disabledStreamIds"
			:has-live-subscription="hasLiveSubscription"
			:mediaflow-resolution="mediaflowResolution"
			:fps-options="fpsOptions"
			:transition-label="transitionLabel"
			@toggle="toggleSchedule"
			@delete="onScheduleDelete"
			@schedule-saved="onScheduleAdded"
			@set-destination="setDestination"
			@remove-destination="setDestination(null)"
			@open-stream="onStreamPage"
		/>

		<MediaflowGraphicSettingsModal
			v-model="graphicSettingsOpen"
			:overlay="graphicSettingsTarget"
			:rows="rowNames"
			:saving="graphicSettingsSaving"
			:error="graphicSettingsError"
			@save="saveOverlaySettings"
		/>

		<MediaflowBreakSettingsModal
			v-model="breakModalOpen"
			:flow="breakModalTarget"
			:layouts="layouts"
			:live-inputs="liveInputs"
			:initial-template="breakModalTemplate"
			:saving="breakModalSaving"
			:error="breakModalError"
			@save="saveBreak"
		/>

		<MediaflowMediaPreviewModal
			v-model="showMediaPlayerModal"
			:media="openMediaPlayerSelected"
			:stream="mediaPlayerStream"
			:media-pulse="mediaPlayerMediaPulse"
			:url="mediaPlayerUrl"
			:portrait="isPortraitResolution"
			:loading="mediaPlayerLoading"
			@close="closeMediaPlayerModal"
		/>

		<demo-video-modal ref="modalDEMOVideo" videoid="cmfns2a1v0wt71d3n7eh7i6bt" />
		<ConfirmModal
			modal-id="delete-mediaflow-content"
			title="Delete this production?"
			message="Scenes, graphics, breaks and automation are deleted. This can't be undone."
			ok-text="Delete"
			cancel-text="Cancel"
			modal-type="danger"
			@modal-confirm="onMediaflowDelete"
		/>
		<ConfirmModal
			modal-id="confirm-go-live"
			title="Go live?"
			:message="`${desk.onAirFlow.value ? `Starts with “${desk.onAirFlow.value.name}” and streams` : 'Streams'} to your destination until you stop it.`"
			ok-text="Go live"
			cancel-text="Not yet"
			modal-type="primary"
			@modal-confirm="onStreamStart"
		/>
		<ConfirmModal
			modal-id="confirm-mobile-send"
			:title="sessionActive ? `Send “${mobileSendTarget?.name ?? ''}” to live?` : `Start with “${mobileSendTarget?.name ?? ''}”?`"
			:message="sessionActive ? `Viewers see it right away (${desk.transitionType.value === 'Cut' ? 'cut' : 'fade'}).` : 'It opens on Program when you go live.'"
			:ok-text="sessionActive ? 'Send to live' : 'Start with this scene'"
			cancel-text="Cancel"
			modal-type="primary"
			@modal-confirm="confirmMobileSend"
		/>
		<ConfirmModal
			modal-id="confirm-send-with-draft"
			title="Unsaved changes on air"
			:message="`${desk.pendingCount.value || 'Some'} ${desk.pendingCount.value === 1 ? 'change is' : 'changes are'} not applied yet. Keep them as a draft or discard them.`"
			ok-text="Keep draft and switch"
			cancel-text="Cancel"
			modal-type="primary"
			@modal-confirm="sendKeepingDraft"
		>
			<FormButton type="link" size="sm" class="mt-3 !px-0 text-xs text-surface-8 hover:text-error-light" data-testid="send-discard-draft" @click="hideModal('confirm-send-with-draft'); sendDiscardingDraft()">
				Discard the draft and switch instead
			</FormButton>
		</ConfirmModal>
		<ConfirmModal
			modal-id="confirm-stop-stream"
			title="Stop the production?"
			message="Viewers lose the feed and automation pauses."
			ok-text="Stop production"
			cancel-text="Keep running"
			modal-type="danger"
			@modal-confirm="onConfirmStopStream"
		/>
		<ConfirmModal
			modal-id="confirm-delete-flow"
			:title="`Delete “${flowToDelete?.name ?? ''}”?`"
			:message="flowToDelete?.kind === 'break' ? 'Schedules that use it stop working.' : 'This can’t be undone.'"
			ok-text="Delete"
			cancel-text="Cancel"
			modal-type="danger"
			@modal-confirm="confirmDeleteFlow"
		/>
		<ConfirmModal
			modal-id="confirm-delete-overlay"
			:title="`Delete “${overlayToDelete?.name ?? ''}”?`"
			:message="overlayToDelete?.live ? 'It’s on air and goes off air now.' : 'Your image or page file is kept.'"
			ok-text="Delete"
			cancel-text="Cancel"
			modal-type="danger"
			@modal-confirm="confirmDeleteOverlay"
		/>
		<ConfirmModal
			modal-id="modal-delete-media"
			:title="`Remove “${mediaToDelete?.name ?? 'this source'}”?`"
			:message="mediaToDelete?.staged ? 'Removed from Preview. Viewers keep seeing it until you apply.' : 'Only removed from this scene.'"
			ok-text="Remove"
			cancel-text="Cancel"
			modal-type="danger"
			@modal-confirm="confirmMediaDelete"
		/>
		<ConfirmModal
			modal-id="confirm-delete-schedule"
			:title="`Delete “${scheduleToDelete?.name ?? ''}”?`"
			message="This can’t be undone."
			ok-text="Delete"
			cancel-text="Cancel"
			modal-type="danger"
			@modal-confirm="confirmDeleteSchedule"
		/>
		<template v-if="mediaflowReady && mediaflow?._id">
			<AddFlowModal :mediaflowId="mediaflow._id" :canvas="canvas" :inputs="sceneInputs" :taken-names="takenMediaNames" :scene-count="layouts.length" ref="addFlowModalRef" @flow-added="onFlowAdded" />
			<EditFlowModal :mediaflowId="mediaflow._id" :flow="flowToEdit" ref="editFlowModalRef" @flow-updated="onFlowUpdated" />
			<AddFlowMediaModal v-if="selectedFlow" :mediaflowId="mediaflow._id" :flowId="selectedFlow._id" :stage="desk.stagesEdits.value" :destination-id="destinationId" ref="addFlowMediaModalRef" @media-added="onMediaAdded" @media-staged="onMediaStaged" />
			<AddFlowMediaModal
				v-if="(selectedFlow || layouts[0]) && capabilities.overlays"
				ref="addGraphicModalRef"
				mode="overlay"
				:mediaflowId="mediaflow._id"
				:flowId="(selectedFlow || layouts[0])._id"
				:canvas="canvas"
				:rows="rowNames"
				@overlay-added="onOverlayAdded"
			/>
			<EditMediaflowModal
				v-if="selectedFlow && slectedFlowMediaSource"
				:mediaflowId="mediaflow._id"
				:flowId="selectedFlow._id"
				:media="slectedFlowMediaSource"
				ref="editMediaflowModalRef"
				:streamAlive="streamAlive || streamAlivePreview"
				:stage="desk.stagesEdits.value"
				:destination-id="destinationId"
				@media-updated="onMediaUpdated"
				@media-staged="onMediaEditStaged"
			/>
		</template>
	</div>
</template>

<style>
.e-sidebar-overlay {
	position: fixed !important;
}

.mf-mobile-draft {
	background-color: color-mix(in srgb, var(--gold-custom) 12%, var(--surface-2));
	--tw-ring-color: color-mix(in srgb, var(--gold-custom) 45%, transparent);
}

/* Go live attention ring: a conic gradient spinning behind the button,
   2 px proud of it, so nothing shifts. Static glow for reduced motion. */
@property --mf-attention-angle {
	syntax: '<angle>';
	initial-value: 0deg;
	inherits: false;
}
.mf-attention {
	position: relative;
	border-radius: 0.375rem;
	isolation: isolate;
}
.mf-attention--on::before {
	content: '';
	position: absolute;
	inset: -2px;
	z-index: -1;
	border-radius: calc(0.375rem + 2px);
	background: conic-gradient(
		from var(--mf-attention-angle),
		transparent 0deg,
		rgba(32, 118, 255, 0.35) 60deg,
		#7db8ff 120deg,
		#ffffff 150deg,
		var(--c-main-light) 185deg,
		transparent 250deg,
		transparent 360deg
	);
	animation: mf-attention-spin 3.2s linear infinite;
}
.mf-attention--on::after {
	content: '';
	position: absolute;
	inset: 0;
	z-index: -2;
	border-radius: 0.375rem;
	box-shadow: 0 0 16px 2px rgba(32, 118, 255, 0.5);
	pointer-events: none;
}
@keyframes mf-attention-spin {
	to {
		--mf-attention-angle: 360deg;
	}
}
@media (prefers-reduced-motion: reduce) {
	.mf-attention--on::before {
		animation: none;
		background: var(--c-main-light);
		opacity: 0.55;
	}
}
</style>
